import { Platform } from 'react-native';
import { Structure } from '../types';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('shardCache');

/**
 * Persistent cache for sharded structure GeoJSON. Indexed by
 * `{serviceAreaId}:{shardId}:{generatedAt}` — a new manifest generation
 * invalidates the whole area's cache automatically. Reads race a 50ms timer
 * and fall through to network if the disk/IndexedDB read hasn't returned by
 * then, so a slow cache never blocks a pan.
 */

const READ_BUDGET_MS = 50;

interface ShardCacheEntry {
  structures: Structure[];
  bytes: number;
  lastAccess: number;
  generatedAt: string;
}

// Lightweight shape check for deserialized cache entries. Full Zod
// validation on every shard (thousands of structures) would tank pan
// performance, so only the envelope is verified here — a tampered
// IndexedDB/filesystem would have to replicate the real Structure shape
// field-by-field to pass downstream usage anyway.
function isValidShardCacheEntry(v: unknown): v is ShardCacheEntry {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    Array.isArray(o.structures) &&
    typeof o.bytes === 'number' &&
    typeof o.lastAccess === 'number' &&
    typeof o.generatedAt === 'string'
  );
}

function cacheKey(areaId: string, shardId: string, generatedAt: string): string {
  return `${areaId}:${shardId}:${generatedAt}`;
}

function raceReadBudget<T>(promise: Promise<T | null>): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<T | null>((resolve) => {
    timer = setTimeout(() => resolve(null), READ_BUDGET_MS);
  });
  return Promise.race([
    promise.finally(() => {
      if (timer) clearTimeout(timer);
    }),
    timeout,
  ]);
}

interface CacheBackend {
  get(key: string): Promise<ShardCacheEntry | null>;
  put(key: string, entry: ShardCacheEntry): Promise<void>;
  listKeys(areaId: string): Promise<string[]>;
  remove(key: string): Promise<void>;
}

// -------- Web backend: IndexedDB (no extra dependency) --------

const DB_NAME = 'redi-shards';
const STORE_NAME = 'shards';
let _webDb: IDBDatabase | null = null;
let _webDbPromise: Promise<IDBDatabase | null> | null = null;

function openWebDb(): Promise<IDBDatabase | null> {
  if (_webDb) return Promise.resolve(_webDb);
  if (_webDbPromise) return _webDbPromise;
  _webDbPromise = new Promise<IDBDatabase | null>((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => {
      _webDb = req.result;
      resolve(req.result);
    };
    req.onerror = () => {
      log.warn('IndexedDB open failed; caching disabled');
      resolve(null);
    };
  });
  return _webDbPromise;
}

const webBackend: CacheBackend = {
  async get(key) {
    const db = await openWebDb();
    if (!db) return null;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const req = tx.objectStore(STORE_NAME).get(key);
        req.onsuccess = () => {
          const raw: unknown = req.result;
          resolve(isValidShardCacheEntry(raw) ? raw : null);
        };
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  },
  async put(key, entry) {
    const db = await openWebDb();
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put(entry, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  },
  async listKeys(areaId) {
    const db = await openWebDb();
    if (!db) return [];
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const req = tx.objectStore(STORE_NAME).getAllKeys();
        req.onsuccess = () => {
          const all = (req.result ?? []) as string[];
          resolve(all.filter((k) => typeof k === 'string' && k.startsWith(`${areaId}:`)));
        };
        req.onerror = () => resolve([]);
      } catch {
        resolve([]);
      }
    });
  },
  async remove(key) {
    const db = await openWebDb();
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  },
};

// -------- Native backend: expo-file-system --------

// expo-file-system v55 ships a class-based API (`Paths` / `File` / `Directory`)
// that replaces the old `documentDirectory` + `*Async` functions. Lazy-load
// via require so the native module isn't bundled on web.
type FsModule = typeof import('expo-file-system');
let _fsModule: FsModule | null = null;
function loadFs(): FsModule | null {
  if (_fsModule) return _fsModule;
  if (Platform.OS === 'web') return null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    _fsModule = require('expo-file-system') as FsModule;
    return _fsModule;
  } catch (err) {
    log.warn('expo-file-system unavailable; native shard caching disabled', err);
    return null;
  }
}

const SHARD_DIR_NAME = 'redi-shards';

function safeFilename(key: string): string {
  return `${key.replace(/[^a-zA-Z0-9._-]/g, '_')}.json`;
}

function shardDir() {
  const fs = loadFs();
  if (!fs) return null;
  return new fs.Directory(fs.Paths.document, SHARD_DIR_NAME);
}

function shardFile(key: string) {
  const fs = loadFs();
  const dir = shardDir();
  if (!fs || !dir) return null;
  return new fs.File(dir, safeFilename(key));
}

const nativeBackend: CacheBackend = {
  async get(key) {
    const file = shardFile(key);
    if (!file || !file.exists) return null;
    try {
      const str = await file.text();
      const parsed: unknown = JSON.parse(str);
      if (!isValidShardCacheEntry(parsed)) return null;
      return parsed;
    } catch (err) {
      log.warn(`shardCache read miss for ${key}`, err);
      return null;
    }
  },
  async put(key, entry) {
    const dir = shardDir();
    const file = shardFile(key);
    if (!dir || !file) return;
    try {
      if (!dir.exists) dir.create({ intermediates: true });
      file.write(JSON.stringify(entry));
    } catch (err) {
      log.warn(`shardCache write failed for ${key}`, err);
    }
  },
  async listKeys(areaId) {
    const dir = shardDir();
    if (!dir || !dir.exists) return [];
    try {
      const safeArea = areaId.replace(/[^a-zA-Z0-9._-]/g, '_');
      return dir
        .list()
        .map((entry) => entry.name)
        .filter((name) => name.startsWith(`${safeArea}_`) && name.endsWith('.json'))
        .map((name) => name.replace(/\.json$/, ''));
    } catch (err) {
      log.warn('shardCache listKeys failed', err);
      return [];
    }
  },
  async remove(key) {
    const file = shardFile(key);
    if (!file || !file.exists) return;
    try {
      file.delete();
    } catch {
      // best-effort
    }
  },
};

const backend: CacheBackend = Platform.OS === 'web' ? webBackend : nativeBackend;

/** Read a shard from cache, racing a hard 50ms budget. */
export async function readCachedShard(
  areaId: string,
  shardId: string,
  generatedAt: string
): Promise<Structure[] | null> {
  const key = cacheKey(areaId, shardId, generatedAt);
  const entry = await raceReadBudget(backend.get(key));
  if (!entry) return null;
  if (entry.generatedAt !== generatedAt) return null;
  // Touch-to-LRU is a write; skip it for the hot read path. Writes refresh
  // lastAccess on their own, and worst case we evict a still-fresh shard.
  return entry.structures;
}

/** Write a shard to cache. Fire-and-forget; never awaited on the hot path. */
export function writeCachedShard(
  areaId: string,
  shardId: string,
  generatedAt: string,
  structures: Structure[]
): void {
  const key = cacheKey(areaId, shardId, generatedAt);
  const bytes = structures.length * 200; // rough estimate; used only for eviction
  const entry: ShardCacheEntry = {
    structures,
    bytes,
    lastAccess: Date.now(),
    generatedAt,
  };
  void backend.put(key, entry).catch((err) => log.warn(`shardCache put failed ${key}`, err));
}

/**
 * Drop stale entries for an area whose manifest generation has changed.
 * Also enforces a soft 50MB cap via LRU eviction. Best-effort; callers
 * should not await on the critical path.
 */
export async function evictStaleShards(areaId: string, currentGeneratedAt: string): Promise<void> {
  try {
    const keys = await backend.listKeys(areaId);
    const deletions: Promise<void>[] = [];
    for (const k of keys) {
      const parts = k.split(':');
      // Native backend stores keys with underscores; skip that case silently.
      if (parts.length < 3) continue;
      const entryGenerated = parts.slice(2).join(':');
      if (entryGenerated !== currentGeneratedAt) {
        deletions.push(backend.remove(k));
      }
    }
    await Promise.all(deletions);
  } catch (err) {
    log.warn(`evictStaleShards failed for ${areaId}`, err);
  }
}
